import { type LatLngTuple } from 'leaflet';
import { IWarning } from 'redux-state/weather-warnings/types';
import { IMarkerV1, MarkerPixelOffset } from 'types/markerTypesV1/marker-v1-type.js';
import { MarkerPillPoint, MarkerTypeName } from 'types/markerTypesV2/m-v2-core.js';
import { IMarkerV2 } from 'types/markerTypesV2/m-v2-general.js';
import { getFirstOfArr, predicateMarkerV1 } from 'utilities/isType.js';
import { getMarkerPolygons } from 'utilities/polygons/utils.js';

export type IMarker<T extends 'Standard' | 'Warning' = 'Warning'> = IMarkerV1<T> | IMarkerV2;

export const getMarkerVersionWarnings = (markers: IMarker[]): IWarning[] => {
  const warnings = markers.map((marker) => {
    if (predicateMarkerV1(marker)) {
      const subjectItem = getFirstOfArr(marker.subject);
      const value = subjectItem?.value;
      if (value && 'warning' in value) {
        return value?.warning ?? [];
      }
      return [];
    }
    return marker.generatedWarningsList ?? [];
  });
  return warnings.flat();
};

export const getMarkerVersionPolygons = (marker: IMarker): LatLngTuple[][] | undefined => {
  const polygon = predicateMarkerV1(marker) ? getMarkerPolygons(marker) : marker.polygons?.points;
  return polygon;
};

export const markerLabel = {
  default: (marker?: IMarker) => {
    if (predicateMarkerV1(marker)) {
      return marker?.label;
    }
    return marker?.label?.text;
  },
  mobile: (marker?: IMarker) => {
    if (predicateMarkerV1(marker)) {
      return marker?.override?.mobileLabel ?? markerLabel.default(marker);
    }
    return marker?.label?.textMobile ?? markerLabel.default(marker);
  },
  hover: (marker?: IMarker) => {
    if (predicateMarkerV1(marker)) {
      return marker?.override?.hoverLabel ?? markerLabel.default(marker);
    }
    return marker?.label?.textHover ?? markerLabel.default(marker);
  },
  viewport: ({ isLarge, marker }: { isLarge: boolean; marker?: IMarker }) => {
    if (isLarge) {
      return markerLabel.default(marker);
    }
    // mobile view fallback to normal label
    return markerLabel.mobile(marker);
  },
};

export const getMarkerVersionType = (marker: IMarker) => {
  if (predicateMarkerV1(marker)) {
    const subjectItem = getFirstOfArr(marker.subject);
    if (subjectItem) {
      return subjectItem.type;
    }
  }
  return undefined;
};

export const getMarkerVersionPosition = (
  marker?: IMarker,
): {
  static?: MarkerPixelOffset;
  offset: IMarkerV2['position']['offset'];
  point: IMarkerV2['position']['point'];
  labelPos: IMarkerV1['labelPos'];
} => {
  if (marker) {
    if (predicateMarkerV1(marker)) {
      return {
        point: marker.point,
        static: marker.left && marker.top ? { left: marker.left, top: marker.top } : undefined,
        offset: marker.pointOffset,
        labelPos: marker.labelPos,
      };
    }
    return {
      point: marker.position.point,
      static:
        marker.position.staticPoint?.left && marker.position.staticPoint?.top
          ? { left: marker.position.staticPoint.left, top: marker.position.staticPoint.top }
          : undefined,
      offset: marker.position.offset,
      labelPos: marker.position.labelPos,
    };
  }
  return {
    point: undefined,
    static: undefined,
    offset: undefined,
    labelPos: undefined,
  };
};

export const getMarkerVersionLinkUrl = (marker?: IMarker) => {
  if (predicateMarkerV1(marker)) {
    const subjectItem = getFirstOfArr(marker.subject);
    if (subjectItem) {
      return subjectItem?.link?.url;
    }
  }
  return typeof marker?.action === 'string' ? marker.action : undefined;
};

/** For use in filters */
export const getMarkerVersionTagsorV1Type = (marker?: IMarker) => {
  if (predicateMarkerV1(marker)) {
    const subjectItem = getFirstOfArr(marker.subject);
    if (subjectItem) {
      const type = subjectItem.type;
      return [type];
    }
    return [];
  }
  return marker?.tags ?? [];
};

export const predicateMarkerV2PillType = (marker: IMarkerV2): marker is IMarkerV2<MarkerPillPoint> => {
  if (marker.type === MarkerTypeName.FilterPill || marker.type === MarkerTypeName.Pill) {
    return true;
  }
  return false;
};
